import { Fragment, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, ChevronUp, ChevronDown, ChevronRight, Pin, Eye, EyeOff, Printer, Settings, RefreshCw, Download } from 'lucide-react'

import { api } from '@/api/client'
import { useFileDownload } from '@/lib/useFileDownload'
import { useToast } from '@/stores/toast'
import { useTabField } from '@/stores/tabState'
import { confirm } from '@/stores/confirm'
import { SalesDisplaySettings } from './SalesDisplaySettings'
import { formatNum, cn } from '@/lib/utils'

interface SubRow {
  partner_id: number; partner_name: string
  prev_day: string; today: string; month_total: string
}
interface Row {
  company_id: number   // 줄의 실제 소속 회사(일일매출 합산으로 다른 회사 블록에 있어도 이월은 이 회사로 저장)
  rollup_key: string; rollup_label: string; partner_ids: number[]
  prev_day: string; today: string; month_total: string
  day_carry: string; carry_input: string; carry_cum: string
  pinned: boolean; sort: number
  hidden: boolean
  subs: SubRow[]
  // 그 달 마감(일일매출 마감): closed = 묶인 업장 전부, closed_partial = 일부만
  closed: boolean; closed_partial: boolean; closed_by: string; closed_at: string
}
interface Sub { prev_day: string; today: string; month_total: string; day_carry: string; carry_cum: string }
interface Block { company_id: number; company_name: string; rows: Row[]; subtotal: Sub; today_input?: string }
interface DailyData {
  date: string; year_month: string
  companies: Block[]
  delivery_268: Block
  jungdomein: Block
  total: Sub; grand_total: Sub
  by_company: Array<{ company_id: number; company_name: string; sales: string; ratio_pct: number }>
  sales_total: string
}

const N = (v: string | number | null | undefined) => formatNum(v)

export function DailySalesTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  // 탭 전환(언마운트)에도 선택 날짜 유지 — 세션 지속.
  const [date, setDate] = useTabField('sales-daily', 'date', '')
  const [showHidden, setShowHidden] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const toggle = (key: string) => setOpen((s) => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n
  })

  const { downloading, download } = useFileDownload()
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['sales-daily', date, showHidden],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (date) qs.set('date', date)
      if (showHidden) qs.set('hidden', '1')
      const s = qs.toString()
      return api<DailyData>(`/api/sales/daily${s ? `?${s}` : ''}`)
    },
  })
  // 첫 응답의 기준일을 date 입력에 반영(사용자가 아직 안 골랐을 때).
  useEffect(() => { if (!date && data?.date) setDate(data.date) }, [data?.date, date])

  const ym = data?.year_month ?? ''

  const saveCarry = useMutation({
    mutationFn: (v: { company: number; rollup_key: string; amount: string; partner_ids: number[] }) =>
      api('/api/sales/carryover', { method: 'POST', body: { ...v, date: data?.date } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-daily'] }),
    onError: (e: any) => push(e?.message || '이월 저장 실패', 'error'),
  })
  // 마감/해제 — 기준일이 속한 달 전체(월초~말일), 줄에 묶인 업장 전부.
  const closeMut = useMutation({
    mutationFn: (v: { partner_ids: number[]; closed: boolean }) =>
      api('/api/sales/close', { method: 'POST', body: { ...v, date: data?.date } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-daily'] }),
    onError: (e: any) => push(e?.message || '마감 처리 실패', 'error'),
  })
  async function toggleClose(r: Row) {
    const month = Number((data?.date ?? '').slice(5, 7))
    const closing = !r.closed
    const n = r.partner_ids.length
    const msg = closing
      ? `'${r.rollup_label}'${n > 1 ? `(업장 ${n}곳)` : ''} ${month}월을 마감할까요?
${month}월 1일~말일 출고전표 등록·수정·삭제와 이월 입력이 잠깁니다.`
      : `'${r.rollup_label}' ${month}월 마감을 해제할까요? 다시 수정할 수 있게 됩니다.`
    if (!(await confirm(msg, { danger: !closing }))) return
    closeMut.mutate({ partner_ids: r.partner_ids, closed: closing })
  }
  const reorder = useMutation({
    mutationFn: (partner_ids: number[]) => api('/api/sales/reorder', { method: 'POST', body: { partner_ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-daily'] }),
    onError: (e: any) => push(e?.message || '순서변경 실패', 'error'),
  })
  const pin = useMutation({
    mutationFn: (v: { partners: number[]; pinned: boolean }) => api('/api/sales/pin', { method: 'POST', body: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-daily'] }),
    onError: (e: any) => push(e?.message || '고정 실패', 'error'),
  })
  const hide = useMutation({
    mutationFn: (v: { partners: number[]; hidden: boolean }) => api('/api/sales/hide', { method: 'POST', body: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-daily'] }),
    onError: (e: any) => push(e?.message || '숨김 처리 실패', 'error'),
  })
  const saveAuction = useMutation({
    mutationFn: (amount: string) => api('/api/sales/auction268', { method: 'POST', body: { date: data?.date, amount } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-daily'] }),
    onError: (e: any) => push(e?.message || '중도매인 저장 실패', 'error'),
  })

  const th = 'px-2 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200'
  const td = 'px-2 py-1 text-sm border border-slate-200 tabular-nums text-right'

  return (
    <div id="sales-print" className="max-w-6xl mx-auto">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #sales-print, #sales-print * { visibility: visible !important; }
        #sales-print { position: absolute; left: 0; top: 0; width: 100%; }
        #sales-print .no-print { display: none !important; }
        #sales-print .print-expand { max-height: none !important; overflow: visible !important; border: 0 !important; }
        /* 인쇄는 촘촘하게(2~3장) — 화면은 그대로. 정렬 열·입력칸 안내문구는 숨김. */
        #sales-print td, #sales-print th { font-size: 11px !important; padding: 1px 4px !important; line-height: 1.25 !important; }
        #sales-print td input { font-size: 11px !important; padding: 0 !important; line-height: 1.25 !important; }
        #sales-print td input::placeholder { color: transparent !important; }
        #sales-print .col-sort, #sales-print .col-close { display: none !important; }
        #sales-print tr.row-closed td { background: transparent !important; }
        #sales-print thead { position: static !important; }
        #sales-print h2 { font-size: 14px !important; }
        /* 당월합계 음영 — 브라우저 '배경 그래픽' 꺼져 있어도 인쇄, 흑백 프린터에도 보이게 회색 */
        #sales-print tr td.col-month, #sales-print tr th.col-month { background: #e5e5e5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        #sales-print tr td.col-month-strong { background: #cccccc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 portrait; margin: 8mm; }
      }`}</style>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-bold text-slate-800">일일 매출 및 미수금 현황</h2>
        <span className="text-sm text-slate-500 tabular-nums">{data?.date}</span>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-slate-400 no-print" />}
        <div className="ml-auto flex items-center gap-2 no-print">
          <button onClick={() => void refetch()} disabled={isFetching}
                  className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  title="출고 수정 후 같은 날짜로 다시 조회">
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} /> 새로고침
          </button>
          <button onClick={() => setSettingsOpen(true)}
                  className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                  title="줄 묶음·숨김 설정">
            <Settings className="w-3.5 h-3.5" /> 표시 설정
          </button>
          <button onClick={() => setShowHidden((v) => !v)}
                  className={cn('flex items-center gap-1 border rounded-lg px-2 py-1 text-xs',
                    showHidden ? 'border-amber-400 text-amber-700 bg-amber-50' : 'border-slate-300 text-slate-600')}
                  title="숨긴 거래처 표시/해제">
            {showHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {showHidden ? '숨김 포함' : '숨김 보기'}
          </button>
          <button onClick={() => window.print()}
                  className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                  title="보고용 인쇄">
            <Printer className="w-3.5 h-3.5" /> 인쇄
          </button>
          <button disabled={downloading || !data}
                  onClick={() => {
                    const qs = new URLSearchParams({ date: data?.date ?? '' })
                    if (showHidden) qs.set('hidden', '1')
                    void download(`/api/sales/daily/export?${qs}`, `일일매출_${data?.date}.xlsx`)
                  }}
                  className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  title="화면 그대로 엑셀로 받기">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} 엑셀
          </button>
          <label className="text-xs text-slate-500">기준일</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="border border-slate-300 rounded-lg px-2 py-1 text-sm tabular-nums" />
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-2 no-print">
        당월합계 = 전일 + 당일. 당일 = 당일매출 − 이월(수기·날짜별)로, 이월 입력 시 당일에서 차감되고 다음달 전일로 넘어갑니다.
      </p>

      <div className="print-expand overflow-auto border border-slate-200 rounded-xl bg-white max-h-[calc(100vh-320px)]">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr>
              <th className={`${th} w-10 text-center col-close`} title="그 달 마감(월초~말일 출고 잠금)">마감</th>
              <th className={`${th} w-10 text-center`}>순번</th>
              <th className={`${th} text-left`}>거래처</th>
              <th className={`${th} w-16 text-center col-sort`}>정렬</th>
              <th className={`${th} text-right`}>전일</th>
              <th className={`${th} text-right`}>당일</th>
              <th className={`${th} text-right bg-emerald-50 col-month`}>당월합계</th>
              <th className={`${th} text-right w-28`}>이월</th>
            </tr>
          </thead>
          <tbody>
            {(data?.companies ?? []).map((b) => {
              const move = (i: number, dir: -1 | 1) => {
                const j = i + dir
                if (j < 0 || j >= b.rows.length) return
                const order = [...b.rows];[order[i], order[j]] = [order[j], order[i]]
                reorder.mutate(order.flatMap((r) => r.partner_ids))
              }
              return (
                <FragmentBlock key={b.company_id} b={b} td={td}>
                  {b.rows.map((r, i) => {
                    const expandable = r.subs.length > 1
                    const isOpen = open.has(r.rollup_key)
                    return (
                    <Fragment key={r.rollup_key}>
                    <tr className={cn('hover:bg-slate-50', r.hidden && 'opacity-50 bg-slate-50/60',
                                      r.closed && 'row-closed [&>td]:bg-slate-200/70')}>
                      <td className={`${td} text-center col-close`}
                          title={r.closed || r.closed_partial
                            ? `마감: ${r.closed_by} ${r.closed_at ? new Date(r.closed_at).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}${r.closed_partial ? ' (일부 업장만 마감)' : ''}`
                            : '체크하면 이 달 출고 등록·수정이 잠깁니다'}>
                        <input type="checkbox" checked={r.closed} disabled={closeMut.isPending}
                               onChange={() => void toggleClose(r)} className="cursor-pointer accent-slate-700"/>
                        {r.closed_partial && <div className="text-[9px] text-amber-600 leading-none">일부</div>}
                      </td>
                      <td className={`${td} text-center text-slate-400`}>{i + 1}</td>
                      <td className={`${td} text-left`}>
                        <div className="flex items-center gap-1">
                          {expandable ? (
                            <button onClick={() => toggle(r.rollup_key)}
                                    className="text-slate-400 hover:text-slate-700 shrink-0 no-print" title="업장별 보기">
                              <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', isOpen && 'rotate-90')} />
                            </button>
                          ) : <span className="inline-block w-3.5 no-print" />}
                          {r.pinned && <Pin className="inline w-3 h-3 text-amber-500 shrink-0" />}
                          <span className="font-medium">{r.rollup_label}</span>
                          {r.hidden && <span className="text-[10px] text-amber-600 border border-amber-300 rounded px-1 no-print">숨김</span>}
                          {expandable && <span className="text-[10px] text-slate-400">({r.subs.length})</span>}
                        </div>
                      </td>
                      <td className={`${td} p-0 col-sort`}>
                        <div className="flex items-center justify-center gap-0.5 no-print">
                          <button onClick={() => move(i, -1)} disabled={i === 0}
                                  className="text-slate-400 hover:text-slate-700 disabled:opacity-20" title="위로">
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => move(i, 1)} disabled={i === b.rows.length - 1}
                                  className="text-slate-400 hover:text-slate-700 disabled:opacity-20" title="아래로">
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => pin.mutate({ partners: r.partner_ids, pinned: !r.pinned })}
                                  className={cn(r.pinned ? 'text-amber-500' : 'text-slate-300 hover:text-slate-500')}
                                  title={r.pinned ? '상단고정 해제' : '상단고정'}>
                            <Pin className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={async () => {
                                    if (!r.hidden && !(await confirm(`'${r.rollup_label}' 줄을 숨길까요? 숨긴 줄은 목록과 합계에서 모두 빠집니다.`, { danger: true }))) return
                                    hide.mutate({ partners: r.partner_ids, hidden: !r.hidden })
                                  }}
                                  className={cn(r.hidden ? 'text-amber-500' : 'text-slate-300 hover:text-slate-500')}
                                  title={r.hidden ? '숨김 해제' : '리스트에서 숨기기'}>
                            {r.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className={td}>{N(r.prev_day)}</td>
                      <td className={td}>{N(r.today)}</td>
                      <td className={`${td} bg-emerald-50 font-semibold col-month`}>{N(r.month_total)}</td>
                      <td className={`${td} p-0 align-top`}>
                        <input
                          key={`${r.rollup_key}-${date}`}
                          type="text" inputMode="decimal"
                          defaultValue={Number(r.carry_input) ? N(r.carry_input) : ''}
                          onFocus={(e) => { e.target.value = e.target.value.replace(/,/g, '') }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                          onBlur={(e) => {
                            const amount = e.target.value.replace(/,/g, '').trim() || '0'
                            if (Number(amount) === Number(r.carry_input)) { e.target.value = Number(amount) ? N(amount) : ''; return }
                            saveCarry.mutate({ company: r.company_id ?? b.company_id, rollup_key: r.rollup_key, amount, partner_ids: r.partner_ids })
                          }}
                          placeholder={r.closed || r.closed_partial ? '마감' : '당일 이월'}
                          disabled={r.closed || r.closed_partial}
                          className="w-full px-2 py-1 text-sm text-right tabular-nums outline-none disabled:bg-transparent disabled:text-slate-500"
                        />
                        {Number(r.carry_cum) !== 0 && (
                          <div className="px-2 pb-0.5 text-[10px] text-slate-400 text-right">누계 {N(r.carry_cum)}</div>
                        )}
                      </td>
                    </tr>
                    {expandable && isOpen && r.subs.map((s) => (
                      <tr key={s.partner_id} className="bg-slate-50/60 text-slate-500">
                        <td className={`${td} col-close`}></td>
                        <td className={td}></td>
                        <td className={`${td} text-left pl-8`}>{s.partner_name}</td>
                        <td className={`${td} col-sort`}></td>
                        <td className={`${td} text-xs`}>{N(s.prev_day)}</td>
                        <td className={`${td} text-xs`}>{N(s.today)}</td>
                        <td className={`${td} text-xs bg-emerald-50/50 col-month`}>{N(s.month_total)}</td>
                        <td className={td}></td>
                      </tr>
                    ))}
                    </Fragment>
                    )
                  })}
                </FragmentBlock>
              )
            })}
            {/* 268호 출고 (자동, 268호 회사 전표 합계) */}
            {data?.delivery_268 && (
              <tr className="bg-slate-100 font-semibold">
                <td className={`${td} col-close`}></td>
                <td className={`${td} text-left`} colSpan={2}>268호 출고 (자동)</td>
                <td className={`${td} col-sort`}></td>
                <td className={td}>{N(data.delivery_268.subtotal.prev_day)}</td>
                <td className={td}>{N(data.delivery_268.subtotal.today)}</td>
                <td className={`${td} bg-emerald-100 col-month-strong`}>{N(data.delivery_268.subtotal.month_total)}</td>
                <td className={td}></td>
              </tr>
            )}
            {/* 중도매인 (수기 입력) */}
            {data?.jungdomein && (
              <tr className="bg-slate-100 font-semibold">
                <td className={`${td} col-close`}></td>
                <td className={`${td} text-left`} colSpan={2}>중도매인 (수기 입력)</td>
                <td className={`${td} col-sort`}></td>
                <td className={td}>{N(data.jungdomein.subtotal.prev_day)}</td>
                <td className={`${td} p-0`}>
                  <input
                    key={`jd-${date}`} type="text" inputMode="decimal"
                    defaultValue={Number(data.jungdomein.today_input) ? N(data.jungdomein.today_input) : ''}
                    onFocus={(e) => { e.target.value = e.target.value.replace(/,/g, '') }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                    onBlur={(e) => {
                      const amount = e.target.value.replace(/,/g, '').trim() || '0'
                      if (Number(amount) === Number(data.jungdomein?.today_input)) { e.target.value = Number(amount) ? N(amount) : ''; return }
                      saveAuction.mutate(amount)
                    }}
                    placeholder="당일 입력"
                    className="w-full px-2 py-1 text-sm text-right tabular-nums outline-none bg-transparent font-semibold" />
                </td>
                <td className={`${td} bg-emerald-100 col-month-strong`}>{N(data.jungdomein.subtotal.month_total)}</td>
                <td className={td}></td>
              </tr>
            )}
            {data && (
              <>
                <tr className="bg-blue-50 font-bold">
                  <td className={`${td} col-close`}></td>
                  <td className={`${td} text-center`} colSpan={2}>총계</td>
                  <td className={`${td} col-sort`}></td>
                  <td className={td}>{N(data.total.prev_day)}</td>
                  <td className={td}>{N(data.total.today)}</td>
                  <td className={`${td} bg-emerald-100 col-month-strong`}>{N(data.total.month_total)}</td>
                  <td className={td}>{N(data.total.carry_cum)}</td>
                </tr>
                <tr className="bg-blue-100 font-bold">
                  <td className={`${td} col-close`}></td>
                  <td className={`${td} text-center`} colSpan={2}>총합계 (총계 + 268호출고 + 중도매인)</td>
                  <td className={`${td} col-sort`}></td>
                  <td className={td}>{N(data.grand_total.prev_day)}</td>
                  <td className={td}>{N(data.grand_total.today)}</td>
                  <td className={`${td} bg-emerald-200 col-month-strong`}>{N(data.grand_total.month_total)}</td>
                  <td className={td}>{N(data.grand_total.carry_cum)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* 사별 매출·비중 (당월 매출 기준) */}
      {data && (
        <div className="mt-5 no-print">
          <div className="text-xs font-semibold text-slate-500 mb-1">사별 매출 · 비중 ({ym} 당월 실출고 기준, 전월이월·이월 제외)</div>
          <table className="border-collapse">
            <thead>
              <tr>
                <th className={th}></th>
                {data.by_company.map((c) => <th key={c.company_id} className={`${th} text-right min-w-[110px]`}>{c.company_name}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={`${td} text-left font-semibold`}>합계매출</td>
                {data.by_company.map((c) => <td key={c.company_id} className={td}>{N(c.sales)}</td>)}
              </tr>
              <tr>
                <td className={`${td} text-left font-semibold`}>비중</td>
                {data.by_company.map((c) => <td key={c.company_id} className={td}>{c.ratio_pct.toFixed(2)}%</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <SalesDisplaySettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}

/** 회사 그룹: 상단 헤더 행 + 각 행(children) + 소계 행. */
function FragmentBlock({ b, td, children }: {
  b: Block; td: string; children: React.ReactNode
}) {
  return (
    <>
      <tr className="bg-slate-50">
        <td colSpan={8} className="px-2 py-1 text-xs font-bold text-slate-700 border border-slate-200">
          {b.company_name} <span className="text-slate-400 font-normal">({b.rows.length})</span>
        </td>
      </tr>
      {children}
      <tr className="bg-slate-50/70 font-semibold">
        <td className={`${td} col-close`}></td>
        <td className={`${td} text-center`} colSpan={2}>소계</td>
        <td className={`${td} col-sort`}></td>
        <td className={td}>{formatNum(b.subtotal.prev_day)}</td>
        <td className={td}>{formatNum(b.subtotal.today)}</td>
        <td className={`${td} bg-emerald-50 col-month`}>{formatNum(b.subtotal.month_total)}</td>
        <td className={td}>{formatNum(b.subtotal.carry_cum)}</td>
      </tr>
    </>
  )
}
